// Manager Approve popup body — renders the shared, config-driven
// FinanceApprovalPanel (type = submissionManager) inside the dynamic popup.
// Admin tags a module's "Approve" action to popupKey "manager-approve".
//
// This backend has NO by-id detail route for submissions/clients/jobs
// (module-data-detail 404s) and its list projections drop the reference IDs,
// so the popup resolves the full picture itself, in-file, from the endpoints
// that DO work:
//   • getFormGroups(submission,id) → recordMeta.{clientId, jobId}
//   • jobs   → getJobDetailView (jobPayDetails = Client Job Rate, jobTitle)
//   • clients   list → clientName (+ vmsFee when the projection exposes it)
//   • employers list → empCompanyName / empCompanyLocation
//   • locations list → taxValue (W2 tax) + name, matched by id OR name
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Drawer, Modal, Radio, Spin, message } from 'antd';
import FinanceApprovalPanel from '../../../finance/FinanceApprovalPanel.jsx';
import { FINANCE_TYPE_SUBMISSION_MANAGER } from '../../../finance/financeApprovalConfig.js';
import { resolveSubmissionFinance, idOf } from '../../../finance/financeApprovalData.js';
import { approveSubmission as approveSubmissionViaVms } from '../../../../services/popupApi';
import {
  getApproveMailDraft,
  sendApproveMail,
} from '../../../../services/mailApproveApi';
import CommonMailComposer from '../../CommonMailComposer.jsx';
import { areValidEmails, parseEmails } from '../../mailUtils.js';

// firstText — first non-blank value. `??` only falls through on null/undefined,
// so an upstream chain that resolves to '' (see submittedBy below) would stop
// dead on it and never reach the fallbacks that exist for exactly that case.
function firstText(...values) {
  return values
    .map((value) => (value === null || value === undefined ? '' : String(value).trim()))
    .find(Boolean) ?? '';
}

const sameText = (a, b) => Boolean(a) && a.toLowerCase() === firstText(b).toLowerCase();

// resolveSubmittedBy — "Submitted By" must name the PERSON who submitted.
// resolveSubmissionFinance's display.submittedBy is a `||` chain that ends in
// `flat.recruiterName || flat.submitSource`, and neither of those is a
// submitter: submitSource is the submission's SOURCE ("Internal", or a vendor)
// — this very function reads it as `vendor` one line down — and recruiterName
// is the ASSIGNED recruiter. Real submissions store submitSource but no
// recruiterName, so display.submittedBy came back as the source string, which
// is truthy, which meant the correct seed.signatureEmail fallback never ran
// and the popup tagged "Internal" as the submitter.
// So: take the resolved value only when it is not one of those source/vendor
// strings, then fall back to the submitter's own signature email.
function resolveSubmittedBy(seed, display, vendor) {
  const resolved = firstText(display.submittedBy);
  const isSourceLeak = sameText(resolved, vendor)
    || sameText(resolved, seed.submitSource)
    || sameText(resolved, seed.supplierName)
    || sameText(resolved, seed.source)
    || sameText(resolved, seed.candidateSource);
  return firstText(isSourceLeak ? '' : resolved, seed.signatureEmail);
}

// Display identity — resolved values (form-groups + joins) win over the
// clicked row, which on the detail surface is flattened and often stale.
function buildIdentity(seed, display = {}) {
  const vendor = display.employerName ?? seed.supplierName ?? seed.submitSource;
  return {
    client: display.clientName ?? seed.clientName,
    role: display.jobTitle ?? seed.jobTitle ?? seed.designation,
    reqId: display.reqId ?? seed.jobClientRefrenceId ?? seed.mspReqId ?? seed.submissionId,
    name: display.candidateName
      || [seed.firstName, seed.lastName].filter(Boolean).join(' ')
      || seed.candidateFullName,
    location: display.jobLocation ?? seed.city ?? seed.currentLocation ?? seed.jobLocation,
    vendor,
    submittedBy: resolveSubmittedBy(seed, display, vendor),
    submittedAt: display.createdAt ?? seed.createdAt,
    email: display.email ?? seed.email,
    employerLocation: display.employerLocation,
    employerName: display.employerName,
  };
}

function mailDetails(addresses) {
  return parseEmails(addresses).map((email) => ({ email }));
}

function normalizeDraft(draft = {}) {
  return {
    to: (draft.to ?? draft.To ?? []).map((item) => item.email ?? item.Email ?? item).filter(Boolean).join(', '),
    cc: (draft.cc ?? draft.Cc ?? []).map((item) => item.email ?? item.Email ?? item).filter(Boolean).join(', '),
    bcc: (draft.bcc ?? draft.Bcc ?? []).map((item) => item.email ?? item.Email ?? item).filter(Boolean).join(', '),
    subject: draft.subject ?? draft.Subject ?? '',
    contents: draft.content?.value ?? draft.Content?.Value ?? draft.contents ?? draft.body ?? '',
  };
}

export default function ManagerApproveBody({ context, selectedIds, selectedRecords, moduleName, onClose }) {
  const seed = selectedRecords?.[0] ?? {};
  const submissionId = selectedIds?.[0] ?? idOf(seed);
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvalChoiceOpen, setApprovalChoiceOpen] = useState(false);
  const [approvalMode, setApprovalMode] = useState('vms');
  const [pendingComputed, setPendingComputed] = useState(null);
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [mailDrawerOpen, setMailDrawerOpen] = useState(false);
  const [mailLoading, setMailLoading] = useState(false);
  const [mailSending, setMailSending] = useState(false);
  const [mail, setMail] = useState({
    to: '', cc: '', bcc: '', subject: '', contents: '', showCc: false, showBcc: false,
  });

  useEffect(() => {
    let cancelled = false;
    resolveSubmissionFinance(submissionId, seed)
      .then((data) => { if (!cancelled) setResolved(data); })
      .catch((error) => {
        if (cancelled) return;
        message.error(error?.message ?? 'Unable to load finance details');
        setResolved({ record: { submission: seed }, lookups: {}, display: {} });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const submitApproval = async () => {
    if (!pendingComputed) return;
    if (approvalMode === 'mail') {
      setApprovalChoiceOpen(false);
      setMailDrawerOpen(true);
      setMailLoading(true);
      try {
        const draft = normalizeDraft(await getApproveMailDraft(submissionId));
        setMail({
          to: draft.to,
          cc: draft.cc,
          bcc: draft.bcc,
          subject: draft.subject,
          contents: draft.contents,
          showCc: Boolean(draft.cc),
          showBcc: Boolean(draft.bcc),
        });
      } catch (error) {
        message.error(error?.message ?? 'Unable to prepare approval mail');
      } finally {
        setMailLoading(false);
      }
      return;
    }

    setApprovalSubmitting(true);
    try {
      await approveSubmissionViaVms(submissionId, {
        candidateProposedRate: pendingComputed.proposedRate,
        candidateProposedRateCurrency: pendingComputed.currencyRaw ?? pendingComputed.currency,
        candidateProposedRateUnit: pendingComputed.rateUnit,
        submitVia: 'VMS',
      });
      message.success('Submission approved');
      setApprovalChoiceOpen(false);
      setPendingComputed(null);
      onClose?.({ success: true });
    } catch (error) {
      message.error(error?.message ?? 'Unable to approve submission');
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const canSendMail = !mailLoading
    && parseEmails(mail.to).length > 0
    && areValidEmails(mail.to)
    && areValidEmails(mail.cc)
    && areValidEmails(mail.bcc)
    && mail.subject.trim()
    && mail.contents.trim();

  const sendMailAndApprove = async () => {
    if (!pendingComputed || !canSendMail || mailSending) return;
    setMailSending(true);
    const approvalPayload = {
      candidateProposedRate: pendingComputed.proposedRate,
      candidateProposedRateCurrency: pendingComputed.currencyRaw ?? pendingComputed.currency,
      candidateProposedRateUnit: pendingComputed.rateUnit,
    };
    try {
      await sendApproveMail({
        submissionId,
        to: mailDetails(mail.to),
        cc: mailDetails(mail.cc),
        bcc: mailDetails(mail.bcc),
        subject: mail.subject || null,
        content: {
          type: 'text/html',
          value: mail.contents || null,
        },
      });
      await approveSubmissionViaVms(submissionId, {
        ...approvalPayload,
        submitVia: 'VMS',
      });
      message.success('Approval mail sent and submission approved');
      setMailDrawerOpen(false);
      setPendingComputed(null);
      onClose?.({ success: true });
    } catch (error) {
      message.error(error?.message ?? 'Unable to send approval mail');
    } finally {
      setMailSending(false);
    }
  };

  const actions = useMemo(() => ({
    submissionApprove: async ({ computed }) => {
      // Unit and currency are written back AS RESOLVED, never as literals.
      // This used to send `candidateProposedRateUnit: 'hr'`, so approving a
      // daily or monthly rate silently rewrote it to hourly — the recruiter's
      // choice was destroyed by the act of approving it. computed.rateUnit is
      // the engine's resolution (submission → candidate budget → job → config
      // default) and computed.currencyRaw is the currency exactly as stored,
      // so an ISO code ("USD") is not rewritten into a symbol on approve.
      setPendingComputed(computed);
      setApprovalMode('vms');
      setApprovalChoiceOpen(true);
    },
    // Reject hands off to the dedicated Reject popup (reason dropdown +
    // comments) instead of rejecting inline — config marks this reject with
    // `openPopup: 'reject'`. Swap this popup for that one over the SAME page
    // (replace, so Back returns to the page, not here); carry the selection so
    // the reject popup's own api.submit targets this submission.
    submissionReject: async () => {
      context?.navigate?.('/popup/reject', {
        replace: true,
        state: {
          background: context?.background ?? null,
          selectedIds,
          selectedRecords,
          moduleName,
        },
      });
    },
  }), [context, selectedIds, selectedRecords, moduleName]);

  if (loading || !resolved) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spin size="large" /></div>;
  }

  return (
    <>
      <FinanceApprovalPanel
        type={FINANCE_TYPE_SUBMISSION_MANAGER}
        record={resolved.record}
        lookups={resolved.lookups}
        identity={buildIdentity(seed, resolved.display)}
        currentUserId={typeof localStorage !== 'undefined' ? localStorage.getItem('userId') ?? undefined : undefined}
        actions={actions}
        onClose={() => onClose?.()}
        variant="popup"
      />
      <Modal
        title="Choose approval method"
        open={approvalChoiceOpen}
        okText="Approve"
        confirmLoading={approvalSubmitting}
        onOk={submitApproval}
        onCancel={() => {
          if (approvalSubmitting) return;
          setApprovalChoiceOpen(false);
          setPendingComputed(null);
        }}
      >
        <Radio.Group
          value={approvalMode}
          onChange={(event) => setApprovalMode(event.target.value)}
          style={{ display: 'grid', gap: 12 }}
        >
          <Radio value="vms">Via VMS</Radio>
          <Radio value="mail">Via Mail</Radio>
        </Radio.Group>
      </Modal>
      <Drawer
        open={mailDrawerOpen}
        placement="right"
        onClose={() => {
          if (mailSending) return;
          setMailDrawerOpen(false);
        }}
        closable={false}
        rootClassName="mail-popup"
        width={620}
        maskClosable={false}
        footer={(
          <div className="mail-popup__footer">
            <Button onClick={() => setMailDrawerOpen(false)} disabled={mailSending}>
              Cancel
            </Button>
            <Button type="primary" loading={mailSending} disabled={!canSendMail} onClick={sendMailAndApprove}>
              Send
            </Button>
          </div>
        )}
      >
        {mailLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <CommonMailComposer
            value={mail}
            onChange={setMail}
            moduleName="submissions"
            ready={!mailLoading}
            title="Submit Via Mail"
            contextRecords={[resolved.record?.submission ?? seed]}
          />
        )}
      </Drawer>
    </>
  );
}

ManagerApproveBody.propTypes = {
  context: PropTypes.object,
  selectedIds: PropTypes.array,
  selectedRecords: PropTypes.array,
  moduleName: PropTypes.string,
  onClose: PropTypes.func,
};
